// RND-5: loose spatial index over entity AABBs. Implemented as a level-hashed loose quadtree:
// an item lives at the depth matching its size, in the cell containing its center; queries
// inflate by half a cell per level (loose factor 2). O(1) insert/remove, cheap range queries.
import type { Bounds } from "./geometry";
import { rectsOverlap } from "./intersect";

const ROOT_SIZE = 2 ** 31; // mm; covers ±1e9 world (EC-RND-1)
const MAX_DEPTH = 12;

interface Item {
  bounds: Bounds;
  key: string;
}

export class Quadtree {
  private items = new Map<string, Item>();
  private cells = new Map<string, Set<string>>();

  get size(): number {
    return this.items.size;
  }

  private static depthFor(bounds: Bounds): number {
    const size = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 1e-3);
    const d = Math.floor(Math.log2(ROOT_SIZE / size));
    return Math.max(0, Math.min(MAX_DEPTH, d));
  }

  private static keyFor(bounds: Bounds): string {
    const depth = Quadtree.depthFor(bounds);
    const cell = ROOT_SIZE / 2 ** depth;
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = (bounds.minY + bounds.maxY) / 2;
    return `${depth}:${Math.floor(cx / cell)}:${Math.floor(cy / cell)}`;
  }

  insert(id: string, bounds: Bounds): void {
    this.remove(id);
    const key = Quadtree.keyFor(bounds);
    this.items.set(id, { bounds, key });
    let set = this.cells.get(key);
    if (!set) {
      set = new Set();
      this.cells.set(key, set);
    }
    set.add(id);
  }

  remove(id: string): void {
    const item = this.items.get(id);
    if (!item) return;
    this.items.delete(id);
    const set = this.cells.get(item.key);
    if (set) {
      set.delete(id);
      if (set.size === 0) this.cells.delete(item.key);
    }
  }

  clear(): void {
    this.items.clear();
    this.cells.clear();
  }

  /** Ids whose stored bounds overlap `query`. */
  query(query: Bounds): string[] {
    const out: string[] = [];
    for (let depth = 0; depth <= MAX_DEPTH; depth++) {
      const cell = ROOT_SIZE / 2 ** depth;
      const half = cell / 2; // loose inflation: item center is in the cell, item extends ≤ half beyond
      const ix0 = Math.floor((query.minX - half) / cell);
      const ix1 = Math.floor((query.maxX + half) / cell);
      const iy0 = Math.floor((query.minY - half) / cell);
      const iy1 = Math.floor((query.maxY + half) / cell);
      // Guard degenerate huge ranges at deep levels: cap cells visited per level.
      if ((ix1 - ix0 + 1) * (iy1 - iy0 + 1) > 4096) continue;
      for (let ix = ix0; ix <= ix1; ix++) {
        for (let iy = iy0; iy <= iy1; iy++) {
          const set = this.cells.get(`${depth}:${ix}:${iy}`);
          if (!set) continue;
          for (const id of set) {
            const item = this.items.get(id)!;
            if (rectsOverlap(item.bounds, query)) out.push(id);
          }
        }
      }
    }
    return out;
  }

  boundsOf(id: string): Bounds | undefined {
    return this.items.get(id)?.bounds;
  }
}
